import config from "../config";
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { getKOTConsole, getKOTDetail } from "../api/orderApi";

// How often the kitchen re-checks the DB for KOTs the live push may have missed.
const KOT_POLL_MS = 15000;

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [kotPrintModal, setKotPrintModal] = useState([]);

  // Track the active connection and any pending reconnect so we never stack
  // multiple EventSources (the old onerror reconnect created untracked ones that
  // cleanup couldn't close, causing duplicate connections and duplicate prints).
  const esRef = useRef(null);
  const reconnectRef = useRef(null);

  // KOT ids we've already queued for print — whether they arrived via the live
  // SSE push or the poll fallback below. Used to avoid printing the same KOT twice.
  const seenKotIdsRef = useRef(new Set());
  // Once true, the poll has recorded the KOTs that already existed on load, so
  // only KOTs created AFTER that baseline trigger a print (no flood on refresh).
  const pollBaselineRef = useRef(false);

  // Helper to create and manage SSE connection
  const initEventSource = useCallback((user) => {
    // Close any existing connection before opening a new one.
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    const params = new URLSearchParams({
      role: String(user.role || "user"),
      restaurantId: String(user.restaurantId || "")
    });

    const eventSource = new EventSource(
      config.API_URL + `/notifications/stream?${params.toString()}`,
      { withCredentials: true }
    );
    esRef.current = eventSource;

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === "KOT_Created" && user.role === config.USER_MANAGER) {
        console.log("KOT notification received:", data);

        const nestedData = data.data || data.payload || data.kot || data;
        const newKotElement = {
          type: "KOT_Created",
          kotId: nestedData.kotId || data.kotId || "N/A",
          seatingName: nestedData.seatingName || nestedData.seating_name || data.seating_name || "N/A",
          products: nestedData.products || data.products || [],
          createdBy: nestedData.createdBy || data.createdBy || "System",
          createdAt: nestedData.createdAt || data.createdAt || new Date().toISOString(),
          timestamp: new Date().toISOString(),
        };

        const idStr = String(newKotElement.kotId);
        const alreadyQueued = idStr !== "N/A" && seenKotIdsRef.current.has(idStr);
        if (idStr !== "N/A") seenKotIdsRef.current.add(idStr);
        if (!alreadyQueued) {
          setKotPrintModal((prevQueue) => [...prevQueue, newKotElement]);
        }
      }

      if (data.type === "KOT_Bill_generate" && user.role === config.USER_MANAGER) {
        console.log("Combined KOT & Bill notification received:", data);

        const nestedData = data.data || data.payload || data.kot || data;

        const kotJob = {
          printType: "KOT",
          kotId: nestedData.kotId || data.kotId || "N/A",
          seatingName: nestedData.seatingName || nestedData.seating_name || data.tableId || "N/A",
          products: nestedData.products || data.products || [],
          createdBy: nestedData.createdBy || data.createdBy || "System",
          createdAt: nestedData.createdAt || data.createdAt || new Date().toISOString(),
        };

        const idStr = String(kotJob.kotId);
        const alreadyQueued = idStr !== "N/A" && seenKotIdsRef.current.has(idStr);
        if (idStr !== "N/A") seenKotIdsRef.current.add(idStr);
        if (!alreadyQueued) {
          setKotPrintModal((prevQueue) => [...prevQueue, kotJob]);
        }
      }

      setNotifications((prev) => [data, ...prev]);
    };

    eventSource.onerror = (err) => {
      console.error("EventSource failed:", err);
      eventSource.close();

      // Only the currently-active connection may trigger a reconnect. A stale
      // EventSource firing onerror must not stomp a healthy live connection.
      if (esRef.current !== eventSource) {
        return;
      }
      esRef.current = null;

      // 🔁 Auto-reconnect after 5 seconds (track the timer so it can be cancelled)
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
      }
      reconnectRef.current = setTimeout(() => {
        console.log("Reconnecting SSE...");
        initEventSource(user);
      }, 5000);
    };

    return eventSource;
  }, []);

  // Correct useEffect at top level
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }

    initEventSource(user);

    return () => {
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
        reconnectRef.current = null;
      }
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [user, initEventSource]);

  // 🛟 Poll fallback: the live SSE push is best-effort (a server restart drops
  // in-memory clients, a brief network blip drops the socket), so a KOT can be
  // created without ever reaching the kitchen screen. Every few seconds we ask
  // the DB for the in-progress KOTs and print any we haven't already seen.
  useEffect(() => {
    if (!user || user.role !== config.USER_MANAGER || !user.restaurantId) {
      return;
    }

    let cancelled = false;
    const rid = user.restaurantId;

    const poll = async () => {
      try {
        const tables = await getKOTConsole(rid);
        if (cancelled || !Array.isArray(tables)) return;

        const ids = tables
          .flatMap((t) => (Array.isArray(t.kots) ? t.kots : []))
          .map((k) => String(k.kot_id))
          .filter((id) => id && id !== "undefined");

        // First successful poll only records what already exists — don't reprint
        // KOTs that were made before this screen opened.
        if (!pollBaselineRef.current) {
          ids.forEach((id) => seenKotIdsRef.current.add(id));
          pollBaselineRef.current = true;
          return;
        }

        const missed = ids.filter((id) => !seenKotIdsRef.current.has(id));
        for (const id of missed) {
          // Claim it up front so the next tick doesn't also enqueue it.
          seenKotIdsRef.current.add(id);
          try {
            const detail = await getKOTDetail(id, rid);
            if (cancelled || !detail || detail.status !== "inprogress") continue;
            console.warn(`KOT ${id} was missed by the live push — printing via poll fallback`);
            setKotPrintModal((prevQueue) => [
              ...prevQueue,
              {
                type: "KOT_Created",
                kotId: detail.kot_id,
                seatingName: detail.seating_name || "N/A",
                products: detail.products || [],
                createdBy: detail.updated_by || "System",
                createdAt: detail.datetime || new Date().toISOString(),
                timestamp: new Date().toISOString(),
              },
            ]);
          } catch (err) {
            // Fetch failed — un-claim so a later poll can retry this KOT.
            seenKotIdsRef.current.delete(id);
            console.error(`Poll fallback failed to load KOT ${id}:`, err);
          }
        }
      } catch (err) {
        console.error("KOT poll fallback failed:", err);
      }
    };

    poll();
    const timer = setInterval(poll, KOT_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
      pollBaselineRef.current = false;
    };
  }, [user]);

  const handleKotPrint = async (printNow = true) => {
    if (printNow && kotPrintModal.length > 0) {
      const job = kotPrintModal[0];
      // KOT_Created jobs carry `type`; KOT_Bill_generate jobs carry `printType`.
      console.log(`Printing dynamic document job type: ${job.printType || job.type || "KOT"}`);
      window.print();
    }

    setKotPrintModal((prevQueue) => prevQueue.slice(1));
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        handleKotPrint,
        kotPrintModal,
        setKotPrintModal
      }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);